import React, { useEffect, useState } from 'react';
import config from '../../../utils/config.js';
import Modal from '../../../components/Modal.js';
import Notification from '../../../components/Notification.js';
import LoadingButtonFixedWidth from '../../../components/LoadingButtonFixedWidth.js';
import { useAuth } from '../../context/wms-context.js';
import logError from '../../../utils/errorUtil.js';

export default function InventoryUpdate() {
    const ORIGIN = config.origin;
    const { currentUser } = useAuth();
    const [inventoryLineNumber, setInventoryLineNumber] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [inventoryLineData, setInventoryLineData] = useState(null);
    const [quantityToUpdate, setQuantityToUpdate] = useState(null);
    const [quantityModal, setQuantityModal] = useState(false);
    const [printModal, setPrintModal] = useState(false);
    const [allocationsData, setAllocationsData] = useState(null);
    const [allocationTotalQuantity, setAllocationTotalQuantity] = useState(null);
    const [showSavedNotification, setShowSavedNotification] = useState({
        shown: false,
        title: '',
        desc: '',
        status: '',
    });

    // Only show the notification for 5 seconds
    useEffect(() => {
        if (showSavedNotification.shown) {
            setTimeout(() => {
                setShowSavedNotification({ shown: false });
            }, 5000);
        }
    }, [showSavedNotification]);

    // Reset the quantity to update when the quantity modal is closed
    useEffect(() => {
        if (!quantityModal) {
            setQuantityToUpdate(null);
        }
    }, [quantityModal]);

    const getInventoryLineData = async (inventoryLine) => {
        setIsLoading(true);
        setInventoryLineData(null);
        try {
            const formattedInventoryLine = inventoryLine.toLowerCase().includes('inv-')
                ? inventoryLine.trim()
                : `INV-${inventoryLine}`.trim();

            const inventoryLineResponse = await fetch(`${ORIGIN}/wms/inventory-update/get-inventory-data`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    inventoryLineName: formattedInventoryLine,
                    authentication: {
                        userId: currentUser.authentication.userId,
                        userEmail: currentUser.authentication.userEmail,
                        userCompany: currentUser.authentication.userCompany || localStorage.getItem('company'),
                    },
                }),
            });

            if (!inventoryLineResponse.ok) {
                throw new Error('Failed to get inventory line data');
            }

            const inventoryLineData = await inventoryLineResponse.json();

            if (!inventoryLineData?.id) {
                setShowSavedNotification({
                    shown: true,
                    title: 'Inventory Line Not Found',
                    desc: 'Inventory Line entered was not found. Please try again.',
                    status: 'question',
                });
                return;
            }

            setAllocationsData(inventoryLineData.allocationAndReleaseData);
            setAllocationTotalQuantity(inventoryLineData.allocationTotalQuantity);
            setInventoryLineData(inventoryLineData);
            setQuantityModal(true);
        } catch (error) {
            logError(error, 'getInventoryLineData');
            setShowSavedNotification({
                shown: true,
                title: 'Error Getting Inventory Line Data',
                desc: 'Failed to get inventory line data, please try again. If the issue persists contact your manager.',
                status: 'error',
            });
            setQuantityModal(false);
        } finally {
            setIsLoading(false);
        }
    };

    const updateInventoryLineData = async () => {
        try {
            if (!quantityToUpdate) {
                setShowSavedNotification({
                    shown: true,
                    title: 'Invalid Update Quantity',
                    desc: 'Please enter a quantity to update.',
                    status: 'question',
                });

                return;
            }

            const inventoryLineResponse = await fetch(`${ORIGIN}/wms/inventory-update/update-inventory-data`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    inventoryLineId: inventoryLineData?.id,
                    quantityToUpdate,
                    authentication: {
                        userId: currentUser.authentication.userId,
                        userEmail: currentUser.authentication.userEmail,
                        userCompany: currentUser.authentication.userCompany || localStorage.getItem('company'),
                    },
                }),
            });

            if (!inventoryLineResponse.ok) {
                throw new Error('Failed to update inventory line data');
            }

            const inventoryLineResponseData = await inventoryLineResponse.json();

            if (inventoryLineResponseData.quantityError) {
                setShowSavedNotification({
                    shown: true,
                    title: 'Invalid Update Quantity',
                    desc: 'Cannot update the overall quantity to be less than is currently allocated for this INV Line.',
                    status: 'question',
                });
                return;
            }

            setQuantityModal(false);
            setPrintModal(true);
            setQuantityToUpdate(null);
            setInventoryLineNumber('');
        } catch (error) {
            logError(error, 'handleSubmit');
            setShowSavedNotification({
                shown: true,
                title: 'Error Updating Inventory Line Quantity',
                desc: 'Failed to update inventory line quantity, please try again. If the issue persists contact your manager.',
                status: 'error',
            });
        }
    };

    const handlePrintInventoryLineData = async () => {
        try {
            const inventoryLineLabelResponse = await fetch(`${ORIGIN}/wms/inventory/print-inventory`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    inventory: [
                        {
                            Id: inventoryLineData?.id,
                            Name: inventoryLineData?.name,
                        },
                    ],
                    authentication: {
                        userId: currentUser.authentication.userId,
                        userEmail: currentUser.authentication.userEmail,
                        userCompany: currentUser.authentication.userCompany || localStorage.getItem('company'),
                    },
                }),
            });

            if (!inventoryLineLabelResponse.ok) {
                throw new Error('Failed to print inventory line data');
            }

            const inventoryLineLabelData = await inventoryLineLabelResponse.blob();
            const inventoryLineLabelFile = new Blob([inventoryLineLabelData], { type: 'application/pdf' });
            const inventoryLineLabelFileURL = URL.createObjectURL(inventoryLineLabelFile);
            window.open(inventoryLineLabelFileURL);

            setPrintModal(false);
        } catch (error) {
            logError(error, 'handlePrintInventoryLineData');
            setShowSavedNotification({
                shown: true,
                title: 'Error Printing Inventory Line Data',
                desc: 'Failed to print inventory line data, please try again. If the issue persists contact your manager.',
                status: 'error',
            });
        }
    };

    // Only allow digits in the quantity to update field
    const handleQuantityUpdateInput = (value) => {
        if (/^\d*$/.test(value)) {
            setQuantityToUpdate(value);
        }
    };

    return (
        <div className='flex h-[calc(100vh-64px)] items-center justify-center'>
            <Notification
                showSavedNotification={showSavedNotification.shown}
                setShowSavedNotification={() => setShowSavedNotification({ shown: false })}
                title={showSavedNotification.title}
                desc={showSavedNotification.desc}
                status={showSavedNotification.status}
            />
            <Modal
                open={quantityModal}
                setOpen={setQuantityModal}
                onConfirm={() => updateInventoryLineData()}
                dontCloseOnConfirm={true}
                title={'Please enter new quantity below'}
                body={
                    <>
                        <a
                            className='text-base text-blue-600 underline cursor-pointer hover:text-blue-800'
                            href={inventoryLineData?.inventoryUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                        >
                            {inventoryLineData?.name}
                        </a>
                        <p className='text-base text-gray-700'>{inventoryLineData?.partName}</p>
                        <div className='flex flex-row gap-10 justify-center items-end mt-2'>
                            <div className='flex flex-col items-center'>
                                <label className='mb-2 text-base text-gray-700'>INV Total Quantity</label>
                                <input
                                    type='number'
                                    value={inventoryLineData?.inventoryQuantity}
                                    disabled
                                    className='w-24 h-24 text-4xl text-center text-black rounded-md border-2 border-black'
                                />
                            </div>
                            <div className='flex flex-col items-center'>
                                <label className='mb-2 text-base text-gray-700'>Quantity Available</label>
                                <input
                                    type='number'
                                    value={inventoryLineData?.inventoryQuantityAvailable}
                                    disabled
                                    className='w-24 h-24 text-4xl text-center text-black rounded-md border-2 border-black'
                                />
                            </div>
                            <div className='flex flex-col items-center'>
                                <label className='mb-2 text-base text-gray-700'>New Quantity</label>
                                <input
                                    type='text'
                                    value={quantityToUpdate}
                                    onChange={(e) => handleQuantityUpdateInput(e.target.value)}
                                    className='w-24 h-24 text-4xl text-center text-black rounded-md border border-gray-500'
                                />
                            </div>
                        </div>
                    </>
                }
                confirmText='Confirm'
                cancelText='Close'
                side={
                    allocationsData && Array.isArray(allocationsData) && allocationsData.length > 0 ? (
                        <div className='overflow-x-auto w-full'>
                            <table className='min-w-full'>
                                <thead className='sticky top-0 z-10 bg-white'>
                                    <tr>
                                        <th className='py-2 pl-1 font-semibold text-center text-white border-b bg-blue-primary'>
                                            Release
                                        </th>
                                        <th className='py-2 font-semibold text-center text-white border-b bg-blue-primary'>
                                            Allocation
                                        </th>
                                        <th className='py-2 pr-1 font-semibold text-center text-white border-b bg-blue-primary'>
                                            Quantity
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {allocationsData.map((allocation, i) => (
                                        <tr key={i} className='even:bg-gray-50'>
                                            <td className='py-1 pl-1 max-w-xs text-center truncate border-b'>
                                                <a
                                                    href={allocation.releaseUrl}
                                                    target='_blank'
                                                    rel='noopener noreferrer'
                                                    className='text-blue-600 underline hover:text-blue-800'
                                                >
                                                    {allocation.releaseName}
                                                </a>
                                            </td>
                                            <td className='px-4 max-w-xs truncate border-b'>
                                                <a
                                                    href={allocation.allocationUrl}
                                                    target='_blank'
                                                    rel='noopener noreferrer'
                                                    className='text-blue-600 underline hover:text-blue-800'
                                                >
                                                    {allocation.allocationName}
                                                </a>
                                            </td>
                                            <td className='px-4 pr-1 text-right text-gray-500 border-b'>
                                                {allocation.quantity}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : undefined
                }
                sideFooter={
                    <div className='sticky bottom-0 z-10 px-4 py-1 font-semibold text-center text-white border-t bg-blue-primary'>
                        <p className='text-center text-white'>Total Allocated Quantity: {allocationTotalQuantity}</p>
                    </div>
                }
            />
            {/* Modal to ask the user if they want to print the Inventory Line tag */}
            <Modal
                open={printModal}
                setOpen={setPrintModal}
                onConfirm={() => handlePrintInventoryLineData()}
                dontCloseOnConfirm={true}
                title={'Print Inventory Line Data'}
                body={'Would you like to print the Inventory Tag?'}
                confirmText='Print'
                cancelText='Close'
            />

            {/* Actual page render */}
            <div className='flex flex-col items-center'>
                <label htmlFor='inventoryLine' className='mb-2 text-lg'>
                    Inventory Line
                </label>
                <input
                    id='inventoryLine'
                    type='text'
                    value={inventoryLineNumber}
                    onChange={(e) => setInventoryLineNumber(e.target.value)}
                    className='p-2 rounded border border-gray-300'
                    autoFocus
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            getInventoryLineData(inventoryLineNumber);
                        }
                    }}
                />
                {!isLoading ? (
                    <button
                        className='px-4 py-2 mt-4 text-white rounded-lg bg-blue-primary'
                        onClick={() => getInventoryLineData(inventoryLineNumber)}
                    >
                        Submit
                    </button>
                ) : (
                    <div className='flex justify-center mt-4'>
                        <LoadingButtonFixedWidth stopLoadingTextChange={true} />
                    </div>
                )}
            </div>
        </div>
    );
}
